import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { resetPassword } from 'actions/Auth';
import { setErrorDetails, setGeneralLoader } from "actions/App";
import { toastManager } from "components/ToastNotification";
import classNames from "classnames";
import TextInput from "components/TextInput";

const PasswordSetup = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const authUser = useSelector((state) => state.auth.authUser);
  const errorDetails = useSelector((state) => state.app.errorDetails);
  const generalLoader = useSelector((state) => state.app.generalLoader);

  const [FirstName, setFirstName] = useState(authUser.firstName);
  const [LastName, setLastName] = useState(authUser.lastName);
  const [Password, setPassword] = useState("");
  const [PasswordVerify, setPasswordVerify] = useState("");
  const [revealOne, setRevealOne] = useState(false);
  const [revealTwo, setRevealTwo] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    document.title = "Reset Password | Smart Entry";
  }, []);

  useEffect(() => {
    return () => {
      // Runs on unmount
      dispatch(setErrorDetails(null));
    };
  }, []);

  useEffect(() => {
    if (errorDetails) {
      setHasError(true);
      setErrorMessage(errorDetails.description);
    } else {
      setHasError(false);
      setErrorMessage("");
    }
  }, [errorDetails]);

  const resetCreds = (e) => {
    e.preventDefault();
    if (FirstName === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.firstName"}));
      return;
    }
    if (LastName === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.lastName"}));
      return;
    }
    if (Password === "") {
      toastManager.addToast(intl.formatMessage({id: "notifications.error.blank.password"}));
      return;
    }
    if (Password !== PasswordVerify) {
      setHasError(true);
      setErrorMessage(intl.formatMessage({id: "auth.reset.newVerifyPassword"}));
      return;
    }
    if (Password.length < 8) {
      setHasError(true);
      setErrorMessage(intl.formatMessage({id: "auth.reset.sort.password"}));
      return;
    }
    const data = {
        authUser,
        FirstName,
        LastName,
        Password,
        PasswordVerify
    };
    dispatch(setGeneralLoader({button: true, text: intl.formatMessage({ id: "pages.appInstructions.loggingIn" })}));
    dispatch(resetPassword(data));
  };

  const measurePasswordStrength = (p) => {
    // First we check length and set strength accordingly
    let strength;
    if (p.length === 8) {
        strength = 1;
    } else if (p.length === 9) {
        strength = 2;
    } else if (p.length === 10) {
        strength = 3;
    } else if (p.length === 11) {
        strength = 4;
    } else if (p.length > 11) {
        strength = 5;
    } else {
        strength = 0;
    }
    // Then we check regex patterns 1. number, 2. special character, 3. uppercase, 4. lowercase
    let patterns = [/(?=.*[0-9])/, /(?=.*[-+_!@#$%^&*()=<>./?])/, /(?=.*[A-Z])/, /(?=.*[a-z])/];
    patterns.forEach(pattern => pattern.test(p) && (strength += 1));
    // ultimately strength < 4 === weak, strength 4-5 === moderate, and strength > 5 strong 
    setPasswordStrength(strength);
  }

  const clearError = () => {
    setHasError(false);
    setErrorMessage("");
  }

  const handleEnter = (e) => {
    if (e.keyCode == 13) {
      resetCreds(e);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <label className="flex justify-between items-center h-10">
        <h5 className="tw-heading-5 font-bold text-black">
          {intl.formatMessage({ id: "auth.reset.header.password" })}
        </h5>
      </label>

      <div className="relative flex flex-col justify-between">
        <form onKeyDown={(e) => handleEnter(e)} autoComplete="off">
          <div className="relative mt-6 w-full flex flex-col gap-4">
            <TextInput
              id="Password-Setup-First-Name-Input"
              dataCy="Password-Setup-First-Name-Input"
              type="text"
              name="text"
              placeholder={`${intl.formatMessage({id: "form.firstName"})}`}
              onChange={(e) => {setFirstName(e.target.value)}}
              value={FirstName}
            />
            <TextInput
              id="Password-Setup-Last-Name-Input"
              dataCy="Password-Setup-Last-Name-Input"
              type="text"
              name="text"
              placeholder={`${intl.formatMessage({id: "form.lastName"})}`}
              onChange={(e) => {setLastName(e.target.value)}}
              value={LastName}
            />
            <TextInput
              id="Password-Setup-Password-One-Input"
              dataCy="Password-Setup-Password-One-Input"
              type={revealOne ? "text" : "password"}
              autoComplete="new-password"
              placeholder={`${intl.formatMessage({id: "auth.reset.password"})}`}
              onChange={(e) => {
                if (hasError) {
                  clearError();
                }
                measurePasswordStrength(e.target.value);
                setPassword(e.target.value)
              }}
              value={Password}
              buttonOneIcon="eye"
              buttonOneAction={(e) => {
                e.preventDefault();
                if (hasError) {
                  clearError();
                }
                setRevealOne(!revealOne);
              }}
            />
            <TextInput
              id="Password-Setup-Password-Two-Input"
              dataCy="Password-Setup-Password-Two-Input"
              type={revealTwo ? "text" : "password"}
              autoComplete="new-password"
              placeholder={`${intl.formatMessage({id: "auth.reset.verifypassword"})}`}
              onChange={(e) => {
                if (hasError) {
                  clearError();
                }
                setPasswordVerify(e.target.value)
              }}
              value={PasswordVerify}
              buttonOneIcon="eye"
              buttonOneAction={(e) => {
                e.preventDefault();
                if (hasError) {
                  clearError();
                }
                setRevealTwo(!revealTwo);
              }}
              buttonTwoIcon={(generalLoader && generalLoader.button) ? "loader20" : "arrowRight"}
              buttonTwoStyle={(generalLoader && generalLoader.button) ? "animate-spin [animation-duration:2000ms]" : ""}
              buttonTwoAction={(e) => (!generalLoader || !generalLoader.button) && resetCreds(e)}
              hasError={hasError ? true : false}
              errorMessage={hasError ? errorMessage : null}
            />
          </div>
        </form>
        {passwordStrength > 0 && <div className="absolute -mb-30 bottom-0 flex flex-col">
          <div className="flex items-center gap-1">
            <div className="h-1 w-15 rounded-xl bg-[#C87D7D]" />
            <div className={classNames(
              "h-1 w-15 rounded-xl",
              passwordStrength > 3 ? "bg-[#D1C054]" : "bg-[var(--color-gray-400)]" )}
            />
            <div className={classNames(
              "h-1 w-15 rounded-xl",
              passwordStrength > 5 ? "bg-[#77BC67]" : "bg-[var(--color-gray-400)]" )}
            />
            <p className="tw-paragraph-reg ml-1">
              {
                passwordStrength > 5
                ?
                intl.formatMessage({ id: 'auth.forgot.passwordStrong' })
                :
                passwordStrength > 3 && passwordStrength < 6
                ?
                intl.formatMessage({ id: 'auth.forgot.passwordModerate' })
                :
                intl.formatMessage({ id: 'auth.forgot.passwordWeak' })
              }
          </p>
          </div>
          <p className="tw-paragraph-reg">
            {intl.formatMessage({ id: 'auth.forgot.passwordTip' })}
          </p>
        </div>}
      </div>
    </div>
  );
};

export default PasswordSetup;